import React, { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowRight, Wand2, Rocket, Sparkles, Image, Bot, Phone, Globe,
  Megaphone, TrendingUp, Target, Workflow, Zap, Search, Database,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PRODUCT_BRAND, productShowcases } from '../data/productShowcases';
import {
  MarketingNav,
  MarketingFooter,
  FloatingOrbs,
  fadeUp,
  AnimatedSection,
} from './IndustryCaseStudy';
import '../styles/landing-clean.css';

const ICONS = {
  Wand2, Rocket, Sparkles, Image, Bot, Phone, Globe,
  Megaphone, TrendingUp, Target, Workflow, Zap, Search, Database,
};

const Products = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <div className="landing-clean min-h-screen overflow-x-hidden" data-testid="products-hub">
      <MarketingNav />

      <section
        ref={heroRef}
        className="relative min-h-[72vh] flex items-center justify-center pt-28 pb-16 px-6 overflow-hidden bg-[#fbbf0f]"
      >
        <FloatingOrbs />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(rgba(28,25,23,0.04) 1px, transparent 1px)',
            backgroundSize: '14px 14px',
          }}
        />
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 text-center max-w-4xl mx-auto">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
            className="font-heading text-sm md:text-base font-extrabold tracking-tight text-[#16130d] mb-4"
          >
            {PRODUCT_BRAND} products
          </motion.p>
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-[#16130d] mb-5"
          >
            Everything to build, market & run your business
          </motion.h1>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
            className="text-lg md:text-xl text-[#3a3327] font-medium max-w-2xl mx-auto mb-9 leading-relaxed"
          >
            Explore each product — then log in to open it inside your workspace.
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="flex flex-wrap items-center justify-center gap-3">
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Button
                onClick={() => navigate('/auth')}
                className="!bg-[#16130d] !text-white hover:!bg-black rounded-full px-8 py-3 text-base font-bold shadow-[0_5px_0_0_rgba(0,0,0,0.35)]"
                data-testid="products-hub-login"
              >
                Login <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Button
                onClick={() => navigate('/auth')}
                className="!bg-white !text-[#16130d] hover:!bg-white border-2 border-[#16130d] rounded-full px-8 py-3 text-base font-bold shadow-[0_5px_0_0_#16130d]"
              >
                Get started free
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      <section className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {productShowcases.map((p, i) => {
            const Icon = ICONS[p.icon] || Sparkles;
            return (
              <AnimatedSection key={p.slug}>
                <motion.div variants={fadeUp} custom={i % 6} whileHover={{ y: -8 }} className="h-full">
                  <Link
                    to={`/products/${p.slug}`}
                    className="group flex flex-col h-full rounded-[1.75rem] border-2 border-[#16130d]/10 bg-[#fffefb] overflow-hidden hover:border-[#16130d]/40 transition-colors shadow-[0_10px_0_0_rgba(22,19,13,0.06)]"
                    data-testid={`product-card-${p.slug}`}
                  >
                    <div className="bg-[#fbbf0f] px-6 pt-6 pb-5 border-b-2 border-[#16130d]/10 relative overflow-hidden">
                      <motion.div
                        className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/30 blur-2xl"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 5 + (i % 3), repeat: Infinity, ease: 'easeInOut' }}
                      />
                      <div className="relative flex items-center justify-between gap-3 mb-3">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#16130d] text-[#fbbf0f] border-2 border-[#16130d] shadow-[0_3px_0_0_rgba(0,0,0,0.25)]">
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] bg-white border-2 border-[#16130d] text-[#16130d] rounded-full px-2.5 py-1">
                          {p.tag}
                        </span>
                      </div>
                      <h2 className="relative font-heading text-xl font-extrabold text-[#16130d] leading-tight">
                        {p.title}
                      </h2>
                      <p className="relative text-sm font-semibold text-[#3a3327] mt-1">{p.subtitle}</p>
                    </div>
                    <div className="px-6 py-5 flex flex-col flex-1">
                      <p className="text-sm text-[#5c5245] leading-relaxed flex-1">{p.desc}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-extrabold text-[#16130d] group-hover:gap-2.5 transition-all">
                        View product <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              </AnimatedSection>
            );
          })}
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-[#fbbf0f] border-y-2 border-[#16130d]/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#16130d] mb-4">
            Ready to open your workspace?
          </h2>
          <p className="text-[#3a3327] font-medium mb-8">
            Login to access every product from one dashboard.
          </p>
          <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block">
            <Button
              onClick={() => navigate('/auth')}
              className="!bg-[#16130d] !text-white rounded-full px-8 py-3 font-bold"
            >
              Login to {PRODUCT_BRAND}
            </Button>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default Products;
